import { FormProvider, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { Box, Button, Divider, Stack } from '@common-components';
import { EmailOutlined as EmailOutlinedIcon } from '@icons';
import { useBoolean, useFormProvider, useToast } from 'hooks';
import { messages } from 'i18n';
import FormTextField from 'components/hookFormComponents/FormTextField';
import { activeTabUrlParamValues, BrokerUrlParams } from 'broker/broker-routes';
import { useUpdateQueryParam } from 'broker/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';

interface IndicationFormProps {
  id: string;
  isContentDirty: boolean;
  setIsContentDirty: (nextState: boolean) => void;
  closeWithoutPrompt: () => void;
}

interface InformationDataProps {
  informationText?: string;
  marketName?: string;
  inboundEmailMessageId?: string;
}

export const schema = yup.object().shape({
  indicationText: yup.string().trim().nullable(),
});

export default function InformationForm({
  id,
  isContentDirty,
  setIsContentDirty,
  closeWithoutPrompt,
  informationText,
  marketName,
  inboundEmailMessageId,
}: IndicationFormProps & InformationDataProps) {
  const { updateSubmissionMarketRequest } = useSubmissionsWorkspace();
  const updateQueryParam = useUpdateQueryParam();

  const { methods } = useFormProvider<InformationDataProps>({
    schema,
    setIsDirty: setIsContentDirty,
    defaultValues: { informationText },
  });

  const { showToast } = useToast();
  const [isRemoving, { set: setIsRemoving }] = useBoolean(false);

  const onSubmit: SubmitHandler<InformationDataProps> = async (data) => {
    await updateSubmissionMarketRequest(id, {
      informationRequested: [
        {
          type: 'Other',
          details: data.informationText || '',
        },
      ],
    });
    showToast('success', {
      message: messages.informationRequestPage.successMessage(marketName || ''),
    });
    closeWithoutPrompt();
  };

  const removeInformationRequest = async () => {
    setIsRemoving(true);
    await updateSubmissionMarketRequest(id, {
      informationRequested: [],
    });
    setIsContentDirty(false);
    closeWithoutPrompt();
  };

  return (
    <Stack>
      <FormProvider {...methods}>
        <Box p={1} pb={0} width={400}>
          <FormTextField
            placeholder={messages.general.addNew}
            maxRows={6}
            minRows={6}
            fullWidth
            multiline
            autoFocus
            name="informationText"
            id="informationText"
            hideBorder
          />
        </Box>
      </FormProvider>
      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
        <Stack>
          {inboundEmailMessageId && (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<EmailOutlinedIcon />}
              onClick={() =>
                updateQueryParam({
                  addParams: [
                    { queryParam: BrokerUrlParams.ACTIVE_TAB, value: activeTabUrlParamValues.EMAIL },
                    { queryParam: BrokerUrlParams.SELECTED_EMAIL, value: inboundEmailMessageId },
                  ],
                })
              }
            >
              {messages.buttons.email}
            </Button>
          )}
        </Stack>

        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1}>
          {informationText && (
            <Button size="small" variant="text" loading={isRemoving} onClick={removeInformationRequest}>
              {messages.buttons.remove}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            loading={methods.formState.isSubmitting}
            disabled={!isContentDirty || methods.formState.isSubmitting}
            onClick={methods.handleSubmit(onSubmit)}
          >
            {informationText ? messages.buttons.update : messages.buttons.add}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
